/**
 * The canonical reading of a `RecipeIngredient` amount, shared by every
 * surface that turns authored recipe amounts into real numbers.
 *
 * Two independent decisions live here, both of which used to be re-made
 * (and re-made DIFFERENTLY) at each call site:
 *
 *  1. **Which measure slot to read.** A `RecipeIngredient` carries four
 *     parallel value/unit pairs — weight, volume, quantity, size — and any
 *     subset may be filled. {@link pickRecipeMeasure} fixes ONE precedence
 *     order for the whole monorepo: weight > volume > quantity > size.
 *     Before this module the purchasing rollup, the frozen order snapshot,
 *     and the chef's recipe page each used a different order, so a line
 *     authored in two slots would have been bought, costed, and cooked at
 *     three different numbers. No production row fills two slots today
 *     (verified 2026-08-31: all 1,241 live rows fill `quantity` only), so
 *     converging is behaviour-preserving — it just closes the gap before
 *     the first two-slot row is authored.
 *
 *     Weight leads because it is the most actionable purchasing unit
 *     ("5 lb chicken" beats "3 cups chicken" beats "5 each"), and size —
 *     the vaguest, most free-text slot — trails.
 *
 *  2. **What the authored number MEANS.** `RecipeIngredient.quantityBasis`
 *     says whether the amount makes the whole batch or one serving:
 *       - `RECIPE_YIELD` (the schema default) — the amount makes the WHOLE
 *         batch of `ComponentRecipe.yieldValue` servings, so one serving is
 *         `amount / baseYield`.
 *       - `PER_SERVING` / `COMPONENT_SERVING` — already one serving.
 *     {@link perServingRecipeQuantity} is the single normalization. A
 *     consumer that multiplies an authored amount by a serving COUNT
 *     without calling this over-orders by `baseYield`x.
 *
 * Zero dependencies and pure, so every surface — server managers, client
 * adapters, and bun tests — can import it directly.
 */

/**
 * A Prisma `Decimal`, or anything else a measure column deserialises to: a
 * raw number, the string a `Decimal` becomes across a JSON boundary, or an
 * object that can restate itself as one (`Decimal` exposes both `toNumber`
 * and `toString`; the object form covers callers typed against either).
 */
export type MeasureValue = { toNumber?: () => number; toString(): string } | number | string | null | undefined

/** The four parallel value/unit pairs on a `RecipeIngredient` row. */
export interface RecipeMeasureSlots {
  sizeValue: MeasureValue
  sizeUnit: string | null | undefined
  volumeValue: MeasureValue
  volumeUnit: string | null | undefined
  weightValue: MeasureValue
  weightUnit: string | null | undefined
  quantityValue: MeasureValue
  quantityUnit: string | null | undefined
}

export interface RecipeMeasure {
  value: number
  unit: string
}

/**
 * Coerce a measure column to a finite number, or null when it carries no
 * usable value. Accepts the three shapes these columns arrive in: a Prisma
 * `Decimal` (has `.toNumber()`), a raw number, or the string a `Decimal`
 * serialises to across a JSON boundary.
 */
export function toMeasureNumber(value: MeasureValue): number | null {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') {
      return null
    }
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  const parsed = typeof value.toNumber === 'function' ? value.toNumber() : Number(value.toString())
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * The monorepo's ONE measure-slot precedence order. Exported so a call
 * site can document (or test) the order it inherits rather than restating
 * it in a comment that can drift.
 */
export const RECIPE_MEASURE_SLOT_ORDER = ['weight', 'volume', 'quantity', 'size'] as const

/**
 * Read the first usable value/unit pair off a `RecipeIngredient` in
 * {@link RECIPE_MEASURE_SLOT_ORDER}. A slot counts only when BOTH its
 * value parses to a finite number AND its unit is a non-empty string — a
 * value with no unit is unusable, and a unit with no value is not a
 * quantity.
 *
 * Returns null when no slot qualifies. Callers apply their own fallback:
 * some render the ingredient name alone, some emit a `0 unit` placeholder
 * beside a "complete this recipe" banner. This function never invents one.
 */
export function pickRecipeMeasure(slots: RecipeMeasureSlots): RecipeMeasure | null {
  const pairs: ReadonlyArray<readonly [MeasureValue, string | null | undefined]> = [
    [slots.weightValue, slots.weightUnit],
    [slots.volumeValue, slots.volumeUnit],
    [slots.quantityValue, slots.quantityUnit],
    [slots.sizeValue, slots.sizeUnit],
  ]
  for (const [value, unit] of pairs) {
    if (unit === null || unit === undefined || unit === '') {
      continue
    }
    const numeric = toMeasureNumber(value)
    if (numeric !== null) {
      return { value: numeric, unit }
    }
  }
  return null
}

/**
 * Whether an authored amount is ALREADY one serving. The inverse — every
 * other value, `RECIPE_YIELD` and unknown/null alike — is a whole-batch
 * amount that must be divided by the yield.
 *
 * Split out from {@link perServingRecipeQuantity} for the one caller that
 * cannot use it: `material-cost-resolver` needs the same basis test but a
 * DIFFERENT missing-yield policy (it reports the cost as unavailable rather
 * than assuming a 1-serving batch — a margin surface must not invent a
 * number). Sharing the test keeps the two from drifting on which bases count
 * as per-serving, which is the part that actually duplicates.
 */
export function isPerServingBasis(quantityBasis: string | null | undefined): boolean {
  return quantityBasis === 'PER_SERVING' || quantityBasis === 'COMPONENT_SERVING'
}

/**
 * The per-serving share of an authored recipe amount, given its
 * `quantityBasis` and the recipe's yield.
 *
 * `PER_SERVING` / `COMPONENT_SERVING` amounts pass through untouched.
 * Everything else — `RECIPE_YIELD`, plus unknown or legacy-null values —
 * is treated as a whole-batch amount and divided by the yield, because
 * `RECIPE_YIELD` is the schema default and an unrecognised basis must not
 * silently inflate a purchase order.
 *
 * The divisor floors at 1: a null/absent/zero yield means "one serving per
 * batch" rather than a division by zero, and a fractional yield (< 1
 * serving per batch) is authoring noise that would MULTIPLY the amount.
 * Flooring keeps the failure direction conservative — never a buy list
 * larger than what was authored. `yieldUnit` is deliberately ignored;
 * yields are authored in servings.
 *
 * NOTE — the three surfaces deliberately differ on a MISSING yield, and the
 * difference is correct in each case. Only the basis test
 * ({@link isPerServingBasis}) is shared:
 *   - PURCHASING (this function) assumes a 1-serving batch. A buy list has to
 *     order something; 91 production recipes carry a null yield, and dropping
 *     their ingredients off the list is worse than the current behaviour,
 *     which is exactly this assumption.
 *   - COST (`material-cost-resolver`) reports the cost as UNAVAILABLE. A
 *     margin surface must not invent a number.
 *   - CHEF DISPLAY (`apps/ordo` `perServingQuantity`) returns null and renders
 *     the ingredient name with no amount — "unknown means unknown", after a
 *     missing yield once printed a 200x multiplier's worth of a batch amount.
 */
export function perServingRecipeQuantity(
  quantity: number,
  quantityBasis: string | null | undefined,
  baseYield: number | null | undefined,
): number {
  if (isPerServingBasis(quantityBasis)) {
    return quantity
  }
  const divisor = baseYield !== null && baseYield !== undefined && Number.isFinite(baseYield) ? baseYield : 1
  return quantity / Math.max(divisor, 1)
}
